import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE = '/api/admin';

const FAQManager = ({ auth }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        sort_order: 0,
        is_enabled: true
    });

    const loadItems = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/faq`, { auth });
            setItems(res.data);
        } catch (err) {
            console.error(err);
            alert('Не удалось загрузить FAQ');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadItems(); }, []);

    const openCreateModal = () => {
        setEditingItem(null);
        setFormData({
            question: '',
            answer: '',
            sort_order: items.length,
            is_enabled: true
        });
        setModalOpen(true);
    };

    const openEditModal = (item) => {
        setEditingItem(item);
        setFormData({
            question: item.question,
            answer: item.answer,
            sort_order: item.sort_order,
            is_enabled: item.is_enabled
        });
        setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async () => {
        try {
            if (editingItem) {
                await axios.patch(`${API_BASE}/faq/${editingItem.id}`, formData, { auth });
            } else {
                await axios.post(`${API_BASE}/faq`, formData, { auth });
            }
            closeModal();
            loadItems();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка сохранения');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить вопрос?')) return;
        try {
            await axios.delete(`${API_BASE}/faq/${id}`, { auth });
            loadItems();
        } catch (err) {
            alert('Ошибка удаления');
        }
    };

    const handleToggleEnabled = async (item) => {
        try {
            await axios.patch(`${API_BASE}/faq/${item.id}`, { is_enabled: !item.is_enabled }, { auth });
            loadItems();
        } catch (err) {
            alert('Ошибка изменения статуса');
        }
    };

    const moveItem = async (index, direction) => {
        const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === sorted.length - 1) return;
        const current = sorted[index];
        const neighbor = sorted[direction === 'up' ? index - 1 : index + 1];
        try {
            await axios.patch(`${API_BASE}/faq/${current.id}`, { sort_order: neighbor.sort_order }, { auth });
            await axios.patch(`${API_BASE}/faq/${neighbor.id}`, { sort_order: current.sort_order }, { auth });
            loadItems();
        } catch (err) {
            alert('Ошибка изменения порядка');
        }
    };

    if (loading) return <div className="text-center py-4">Загрузка...</div>;

    const sortedItems = [...items].sort((a, b) => a.sort_order - b.sort_order);

    return (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl">Вопросы и ответы (Q&A)</h2>
                <button onClick={openCreateModal} className="bg-gold-accent text-white px-4 py-2 rounded hover:bg-yellow-600">
                    + Добавить вопрос
                </button>
            </div>

            {sortedItems.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Нет вопросов. Создайте первый.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border">
                        <thead>
                            <tr className="bg-gray-100 border-b">
                                <th className="text-left p-3">Вопрос</th>
                                <th className="text-left p-3">Ответ</th>
                                <th className="text-left p-3">Порядок</th>
                                <th className="text-left p-3">Активен</th>
                                <th className="text-left p-3">Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedItems.map((item, idx) => (
                                <tr key={item.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 max-w-md truncate">{item.question}</td>
                                    <td className="p-3 max-w-md truncate">{item.answer}</td>
                                    <td className="p-3">{item.sort_order}</td>
                                    <td className="p-3">
                                        <button onClick={() => handleToggleEnabled(item)}
                                            className={`px-2 py-1 rounded text-sm ${item.is_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {item.is_enabled ? 'Вкл' : 'Выкл'}
                                        </button>
                                    </td>
                                    <td className="p-3 flex gap-2">
                                        <button onClick={() => openEditModal(item)} className="text-blue-600" title="Редактировать">✏️</button>
                                        <button onClick={() => handleDelete(item.id)} className="text-red-600" title="Удалить">🗑️</button>
                                        {idx > 0 && <button onClick={() => moveItem(idx, 'up')} className="text-gray-600" title="Вверх">⬆️</button>}
                                        {idx < sortedItems.length - 1 && <button onClick={() => moveItem(idx, 'down')} className="text-gray-600" title="Вниз">⬇️</button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-xl font-bold mb-4">{editingItem ? 'Редактировать вопрос' : 'Новый вопрос'}</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 mb-1">Вопрос</label>
                                    <textarea name="question" value={formData.question} onChange={handleFormChange}
                                        rows="2" className="w-full p-2 border rounded" required />
                                </div>
                                <div>
                                    <label className="block text-gray-700 mb-1">Ответ (HTML разрешён)</label>
                                    <textarea name="answer" value={formData.answer} onChange={handleFormChange}
                                        rows="4" className="w-full p-2 border rounded" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 mb-1">Порядок (меньше – выше)</label>
                                    <input type="number" name="sort_order" value={formData.sort_order} onChange={handleFormChange}
                                        className="w-32 p-2 border rounded" />
                                </div>
                                <div>
                                    <label className="flex items-center gap-2">
                                        <input type="checkbox" name="is_enabled" checked={formData.is_enabled} onChange={handleFormChange} />
                                        <span>Активен (показывать гостям)</span>
                                    </label>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button onClick={closeModal} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Отмена</button>
                                <button onClick={handleSubmit} className="px-4 py-2 bg-gold-accent text-white rounded hover:bg-yellow-600">
                                    {editingItem ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FAQManager;